/**
 * Filter state of the inventory report workbench and its translation into a request (batch 8 T14c, #3036).
 *
 * The server owns every rule (tenant clock, 731-day window, `to` not in the future, `0 < a < b <= 100`); this file
 * only refuses what is plainly malformed BEFORE a request is made, with the same bounds as the API's schema, so a
 * value is never trimmed, clamped or coerced on its way out: whitespace is not removed, it is refused. An empty
 * field means "not set" and is left out. Paging (`limit`, `offset`) is added by the caller, so a changed filter
 * starts again at the first page.
 */
import { PRICE_KINDS, type DeadSlowStatus, type PriceKind, type ReportKind, type ReportParams } from '@/hooks/useInventoryReports';
import type { ReportsAccess } from './reportsSession';

export const MAX_PART_IDS = 100;
export const DEAD_DAYS_MAX = 3650;
export const SLOW_COVERAGE_DAYS_MAX = 3650;
export const WINDOW_DAYS_MAX = 731;
export const MIN_YEAR = 2000;
export const MAX_YEAR = 2100;
export const DEAD_SLOW_STATUSES: readonly DeadSlowStatus[] = ['dead', 'slow', 'active', 'no_history'];
export const PAGE_SIZES = [25, 50, 100] as const;

export interface Picked {
  id: string;
  /** What the user chose it by (a name or a number), kept so the chip never shows an id. */
  label: string;
}

export interface FilterState {
  /** abc: cost | units | revenue; turnover: cost | units. */
  basis: 'cost' | 'units' | 'revenue';
  from: string;
  to: string;
  aThreshold: string;
  bThreshold: string;
  category: Picked | null;
  parts: Picked[];
  /** turnover: part | category; profitability: part | category | booking; valuation: part | category | warehouse. */
  groupBy: string;
  deadDays: string;
  slowCoverageDays: string;
  windowDays: string;
  includeValue: boolean;
  statuses: DeadSlowStatus[];
  warehouse: Picked | null;
  booking: Picked | null;
  partyKind: 'person' | 'company';
  party: Picked | null;
  supplier: Picked | null;
  includePriceVariance: boolean;
  part: Picked | null;
  kinds: PriceKind[];
  asOfMode: 'current' | 'date' | 'instant';
  asOfDate: string;
  asOfInstant: string;
}

/** The intended mode: money for someone who may read it, units for someone who may not. */
export function defaultFilters(access: ReportsAccess): FilterState {
  const financial = access.canFinancial;
  return {
    basis: financial ? 'cost' : 'units',
    from: '',
    to: '',
    aThreshold: '',
    bThreshold: '',
    category: null,
    parts: [],
    groupBy: 'part',
    deadDays: '',
    slowCoverageDays: '',
    windowDays: '',
    includeValue: financial,
    statuses: [],
    warehouse: null,
    booking: null,
    partyKind: 'person',
    party: null,
    supplier: null,
    includePriceVariance: financial,
    part: null,
    kinds: financial ? [...PRICE_KINDS] : ['sell'],
    asOfMode: 'current',
    asOfDate: '',
    asOfInstant: '',
  };
}

/** `YYYY-MM-DD` that is a real calendar date inside the API's bounded years. */
export function isReportDate(text: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  const check = new Date(Date.UTC(year, month - 1, day));
  return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day;
}

const INSTANT = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(?:Z|[+-](\d{2}):(\d{2}))$/;

/**
 * An exact instant the API's format allows AND that exists: a real calendar day (in the API's years), hour 0-23,
 * minute and second 0-59, an offset of at most 23:59. `Date` is no judge of this (it rolls 2026-02-30 over to March),
 * and it only has millisecond precision, so the text is checked as written and sent unchanged, microseconds included.
 */
export function isReportInstant(text: string): boolean {
  const match = INSTANT.exec(text);
  if (!match) return false;
  const [, day = '', hour, minute, second, offsetHour, offsetMinute] = match;
  const within = (value: string | undefined, max: number) => value === undefined || Number(value) <= max;
  return isReportDate(day) && within(hour, 23) && within(minute, 59) && within(second, 59) && within(offsetHour, 23) && within(offsetMinute, 59);
}
const PERCENT = /^\d{1,3}(\.\d{1,4})?$/;

/** Number of the exact decimal text, only to COMPARE two thresholds the user typed (never shown or sent). */
const percentValue = (text: string): number => Number(text);

export type FieldErrors = Partial<Record<string, string>>;

export type BuiltRequest = { ok: true; params: ReportParams } | { ok: false; errors: FieldErrors };

function integerError(text: string, min: number, max: number): string | null {
  if (!/^\d{1,9}$/.test(text)) return 'integer';
  const value = Number(text);
  return value < min || value > max ? 'range' : null;
}

/** Window fields shared by several reports. */
function windowFields(f: FilterState, errors: FieldErrors, out: Record<string, string>): void {
  if (f.from !== '') {
    if (isReportDate(f.from)) out['from'] = f.from;
    else errors['from'] = 'date';
  }
  if (f.to !== '') {
    if (isReportDate(f.to)) out['to'] = f.to;
    else errors['to'] = 'date';
  }
  if (errors['from'] === undefined && errors['to'] === undefined && f.from !== '' && f.to !== '' && f.from > f.to) errors['to'] = 'order';
}

function scopeFields(f: FilterState, errors: FieldErrors, out: Record<string, string>, withParts: boolean): void {
  if (f.category !== null) out['categoryId'] = f.category.id;
  if (withParts && f.parts.length > 0) {
    if (f.parts.length > MAX_PART_IDS) errors['parts'] = 'tooMany';
    else out['partIds'] = f.parts.map((p) => p.id).join(',');
  }
}

/**
 * The request of `kind` for these filters. Which fields exist is per report, exactly as the API's strict schemas
 * allow (an unknown key is a 400): a field of another report is never sent.
 */
export function buildParams(kind: ReportKind, f: FilterState): BuiltRequest {
  const errors: FieldErrors = {};
  const out: Record<string, string> = {};
  switch (kind) {
    case 'abc': {
      out['basis'] = f.basis;
      windowFields(f, errors, out);
      for (const field of ['aThreshold', 'bThreshold'] as const) {
        if (f[field] === '') continue;
        if (PERCENT.test(f[field])) out[field] = f[field];
        else errors[field] = 'percent';
      }
      if (errors['aThreshold'] === undefined && errors['bThreshold'] === undefined) {
        const a = f.aThreshold === '' ? null : percentValue(f.aThreshold);
        const b = f.bThreshold === '' ? null : percentValue(f.bThreshold);
        if (a !== null && (a <= 0 || a > 100)) errors['aThreshold'] = 'thresholdOrder';
        else if (b !== null && (b <= 0 || b > 100)) errors['bThreshold'] = 'thresholdOrder';
        else if (a !== null && b !== null && a >= b) errors['bThreshold'] = 'thresholdOrder';
      }
      scopeFields(f, errors, out, true);
      break;
    }
    case 'turnover': {
      out['basis'] = f.basis === 'units' ? 'units' : 'cost';
      out['groupBy'] = f.groupBy === 'category' ? 'category' : 'part';
      windowFields(f, errors, out);
      scopeFields(f, errors, out, true);
      break;
    }
    case 'dead-and-slow': {
      const ints: [keyof FilterState, string, number][] = [
        ['deadDays', 'deadDays', DEAD_DAYS_MAX],
        ['slowCoverageDays', 'slowCoverageDays', SLOW_COVERAGE_DAYS_MAX],
        ['windowDays', 'windowDays', WINDOW_DAYS_MAX],
      ];
      for (const [field, name, max] of ints) {
        const text = f[field] as string;
        if (text === '') continue;
        const error = integerError(text, 1, max);
        if (error === null) out[name] = text;
        else errors[name] = error;
      }
      out['includeValue'] = String(f.includeValue);
      if (f.statuses.length > 0) out['statuses'] = f.statuses.join(',');
      if (f.warehouse !== null) out['warehouseId'] = f.warehouse.id;
      scopeFields(f, errors, out, true);
      break;
    }
    case 'profitability': {
      out['groupBy'] = f.groupBy === 'category' || f.groupBy === 'booking' ? f.groupBy : 'part';
      windowFields(f, errors, out);
      if (f.booking !== null) out['bookingId'] = f.booking.id;
      if (f.party !== null) {
        out['partyKind'] = f.partyKind;
        out['partyId'] = f.party.id;
      }
      scopeFields(f, errors, out, true);
      break;
    }
    case 'supplier-performance': {
      windowFields(f, errors, out);
      if (f.supplier !== null) out['supplierId'] = f.supplier.id;
      out['includePriceVariance'] = String(f.includePriceVariance);
      break;
    }
    case 'price-history': {
      if (f.part === null) errors['part'] = 'required';
      else out['partId'] = f.part.id;
      if (f.kinds.length === 0) errors['kinds'] = 'required';
      else out['kind'] = PRICE_KINDS.filter((k) => f.kinds.includes(k)).join(',');
      windowFields(f, errors, out);
      if (f.supplier !== null) out['supplierId'] = f.supplier.id;
      break;
    }
    case 'valuation': {
      out['groupBy'] = f.groupBy === 'category' || f.groupBy === 'warehouse' ? f.groupBy : 'part';
      if (f.asOfMode === 'date') {
        if (f.asOfDate === '') errors['asOf'] = 'required';
        else if (isReportDate(f.asOfDate)) out['asOf'] = f.asOfDate;
        else errors['asOf'] = 'date';
      } else if (f.asOfMode === 'instant') {
        if (f.asOfInstant === '') errors['asOf'] = 'required';
        else if (isReportInstant(f.asOfInstant)) out['asOf'] = f.asOfInstant;
        else errors['asOf'] = 'instant';
      }
      if (f.warehouse !== null) out['warehouseId'] = f.warehouse.id;
      scopeFields(f, errors, out, true);
      break;
    }
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, params: out };
}

/** The same request with the page it asks for. `offset` is a multiple of nothing: the server's real offset. */
export function withPage(params: ReportParams, limit: number, offset: number): ReportParams {
  return { ...params, limit: String(limit), offset: String(offset) };
}

/** The offset of the last page that exists for a result of `total` rows, for a user who was past the end after a change. */
export function lastPageOffset(total: number, limit: number): number {
  return total <= 0 ? 0 : Math.floor((total - 1) / limit) * limit;
}

/** True when the server answered a page that starts at or after the end of the result and returned no row. */
export function isPastEnd(pagination: { total: number; offset: number }, rows: number): boolean {
  return rows === 0 && pagination.offset > 0 && pagination.offset >= pagination.total;
}

/** The rows a page really shows, 1-based and inclusive, from the server's offset and the rows it returned; `null` for no rows. */
export function displayedRange(offset: number, rows: number): { from: number; to: number } | null {
  return rows > 0 ? { from: offset + 1, to: offset + rows } : null;
}

/** Where "previous" goes: the last page that exists when the offset is past the end, else one page back without going below 0. */
export function previousOffset(pagination: { total: number; offset: number; limit: number }, rows: number): number {
  if (isPastEnd(pagination, rows)) return lastPageOffset(pagination.total, pagination.limit);
  return Math.max(0, pagination.offset - pagination.limit);
}

/** Where "next" goes: one page of the server's `limit` on, the page step every report route implements; the rows a page returned only change what its range says. */
export function nextOffset(pagination: { offset: number; limit: number }): number {
  return pagination.offset + pagination.limit;
}

/** Which kinds of price history this owner may ask for (only `sell` without the financial right). */
export function allowedPriceKinds(access: ReportsAccess): readonly PriceKind[] {
  return access.canFinancial ? PRICE_KINDS : ['sell'];
}
